import React, {
	type FocusEventHandler,
	type KeyboardEventHandler,
	type MouseEventHandler,
	type ReactNode,
	useCallback,
	useRef,
	useState,
	useEffect,
	useContext,
} from 'react';
import { mergeRefs } from 'use-callback-ref';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading/heading';
import AkImage from '@atlaskit/image';
import { useThemeObserver } from '@atlaskit/tokens';
import { LoomAttributionLogo } from '@atlaskit/logo';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import type { PopupProps, TriggerProps as PopupTriggerProps } from '@atlaskit/popup/types';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer -- Requested suppression for existing Popup import
import Popup from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Inline from '@atlaskit/primitives/inline';
import Stack from '@atlaskit/primitives/stack';
import Text from '@atlaskit/primitives/text';
import { xcss } from '@atlaskit/primitives/xcss';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightMedia,
	SpotlightPrimaryAction,
	SpotlightDismissControl,
	type Placement,
} from '@atlaskit/spotlight';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { LOOM_CROSS_SELL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { expValEquals } from '@confluence/feature-experiments';

import LoomCrossSellPopupCardImage from './images/LoomCrossSellPopupCardImage.png';
import LoomCrossSellPopupCardImageNewVisual_Light from './images/LoomCrossSellPopupCardImageNewVisual_Light.png';
import LoomCrossSellPopupCardImageNewVisual_Dark from './images/LoomCrossSellPopupCardImageNewVisual_Dark.png';

export const TOGGLE_DELAY_TIME_MS = 400;

const i18n = defineMessages({
	tryNow: {
		id: 'loom-utils.loom-cross-sell-popup-card.tryNow',
		defaultMessage: 'Try now',
		description: 'text on button that launches a cross-sell flow for Loom',
	},
});

const cardStyles = xcss({
	borderRadius: 'radius.small',
	overflow: 'auto',
	width: '352px',
});

const cardContentStyles = xcss({
	padding: 'space.300',
});

const imageContainerStyles = xcss({
	height: '201px',
});

const spotlightContentContainerStyles = xcss({
	position: 'fixed',
});

export type EntryPointId =
	| 'loom-confluence-comment-editor-toolbar'
	| 'loom-confluence-create-menu'
	| 'loom-confluence-inline-comment'
	| 'loom-confluence-inline-comment-whiteboard'
	| 'loom-confluence-inline-comment-database'
	| 'loom-confluence-inline-comment-blogpost'
	| 'loom-confluence-page-editor-toolbar'
	| 'loom-confluence-page-header';

export interface TriggerProps extends PopupTriggerProps {
	isSelected?: boolean;
	onBlur?: FocusEventHandler;
	onFocus?: FocusEventHandler;
	onKeyDown?: KeyboardEventHandler;
	onMouseEnter?: MouseEventHandler;
	onMouseLeave?: MouseEventHandler;
}

export type BaseLoomPopupCardProps = {
	actions?: ReactNode;
	description: string;
	entryPointId: EntryPointId;
	placement?: PopupProps['placement'];
	shouldRenderToParent?: boolean;
	title: string;
	trigger: (triggerProps: TriggerProps) => ReactNode;
	sourceAdType?: string;
	sourceComponent?: string;
	spotlightPlacement?: Placement;
	primaryOnClick?: () => void;
	adControl?: ReactNode;
};

export const BaseLoomPopupCard = ({
	actions,
	description,
	entryPointId,
	placement,
	spotlightPlacement,
	shouldRenderToParent = true,
	title,
	trigger,
	sourceAdType,
	sourceComponent,
	primaryOnClick,
	adControl,
}: BaseLoomPopupCardProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';
	const intl = useIntl();

	const [isOpen, setIsOpen] = useState(false);

	const hasStartedPreloadingImage = useRef<boolean>(false);
	const closePopupTimerRef = useRef<NodeJS.Timeout | null>(null);
	const openPopupTimerRef = useRef<NodeJS.Timeout | null>(null);

	const contentWrapperElementRef = useRef<HTMLElement | null>(null);
	const triggerElementRef = useRef<HTMLElement | null>(null);

	const isOpenOnFocusDisabled = useRef(false);

	const clearClosePopupTimer = useCallback(() => {
		if (closePopupTimerRef.current) {
			clearTimeout(closePopupTimerRef.current);
			closePopupTimerRef.current = null;
		}
	}, []);

	const clearOpenPopupTimer = useCallback(() => {
		if (openPopupTimerRef.current) {
			clearTimeout(openPopupTimerRef.current);
			openPopupTimerRef.current = null;
		}
	}, []);

	const experienceTracker = useContext(ExperienceTrackerContext);

	useEffect(() => {
		if (isOpen) {
			experienceTracker.start({
				name: LOOM_CROSS_SELL_EXPERIENCE,
				attributes: {
					sourceComponent: entryPointId,
					adType: sourceAdType,
				},
			});
		}
	}, [entryPointId, experienceTracker, isOpen, sourceAdType]);

	/**
	 * Temporarily disable the popup from re-opening (for a tick) on focus
	 * because in many cases, closing the popup returns focus back to the
	 * trigger, which would normally open the popup again.
	 */
	const safeClosePopup = useCallback(() => {
		setIsOpen(false);
		experienceTracker.abort({
			name: LOOM_CROSS_SELL_EXPERIENCE,
			reason: 'Loom pop up closed',
			attributes: {
				sourceComponent: entryPointId,
				adType: sourceAdType,
			},
		});
		isOpenOnFocusDisabled.current = true;
		setTimeout(() => {
			isOpenOnFocusDisabled.current = false;
		}, 0);
	}, [entryPointId, experienceTracker, sourceAdType]);

	const startClosePopupTimer = useCallback(() => {
		clearOpenPopupTimer();
		if (!closePopupTimerRef.current) {
			closePopupTimerRef.current = setTimeout(() => {
				safeClosePopup();
				closePopupTimerRef.current = null;
			}, TOGGLE_DELAY_TIME_MS);
		}
	}, [clearOpenPopupTimer, safeClosePopup]);

	/**
	 * Don't open the popup card immediately on hover or focus
	 *
	 * 1) Focus: We want KB users to be able to quickly navigate through areas with entrypoints that
	 * are alongside many other UI controls (e.g., Page Header, Page Editor Toolbar) without triggering
	 * the popup card and getting "stuck" (needing to hit "esc" to continue).
	 *
	 * 2) Hover: We don't want users to feel like the popup card gets in their way of clicking other
	 * things in the UI just because their mouse briefly brushed over the entrypoint button.
	 *
	 * This was heavily inspired by the hover cards used in the Page Tree (`useHoverPageCard`)
	 */
	const startOpenPopupTimer = useCallback(() => {
		clearClosePopupTimer();
		if (!hasStartedPreloadingImage.current) {
			hasStartedPreloadingImage.current = true;
			const img = new Image();
			img.src = LoomCrossSellPopupCardImage;
		}
		if (!openPopupTimerRef.current) {
			openPopupTimerRef.current = setTimeout(() => {
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'loomAdPopupCard',
						attributes: {
							adType: sourceAdType,
							entrypoint: entryPointId,
							sourceComponent,
						},
					},
				}).fire();

				setIsOpen(true);
				openPopupTimerRef.current = null;
			}, TOGGLE_DELAY_TIME_MS);
		}
	}, [clearClosePopupTimer, createAnalyticsEvent, entryPointId, sourceAdType, sourceComponent]);

	const handleTriggerBlur = useCallback(() => {
		clearOpenPopupTimer();
	}, [clearOpenPopupTimer]);

	const handleTriggerFocus = useCallback(() => {
		if (expValEquals('cc_comments_a11y_tuneup', 'isEnabled', true)) {
			// Keyboard focus should NOT open the popup — only hover should.
			// Opening on focus violates WCAG 3.2.1 (On Focus) as it triggers a
			// change of context without user activation. (A11Y-30783, A11Y-30827)
			return;
		}
		if (!isOpenOnFocusDisabled.current) {
			startOpenPopupTimer();
		}
	}, [startOpenPopupTimer]);

	const handleTriggerMouseEnter = useCallback(() => {
		startOpenPopupTimer();
	}, [startOpenPopupTimer]);

	const handleCardMouseEnter = useCallback(() => {
		clearClosePopupTimer();
	}, [clearClosePopupTimer]);

	const handleMouseLeave = useCallback(() => {
		/**
		 * Don't close the popup card when moving the mouse outside of it (or the trigger) if something
		 * inside it (or the trigger itself) is focused
		 */
		if (
			!contentWrapperElementRef.current?.contains(document.activeElement) &&
			document.activeElement !== triggerElementRef.current
		) {
			startClosePopupTimer();
		}
	}, [startClosePopupTimer]);

	const content = () => (
		<Box
			onMouseEnter={handleCardMouseEnter}
			onMouseLeave={handleMouseLeave}
			ref={contentWrapperElementRef}
			xcss={cardStyles}
		>
			<Box xcss={imageContainerStyles}>
				<AkImage alt="" height="201px" width="352px" src={LoomCrossSellPopupCardImage} />
			</Box>
			<Stack space="space.300" xcss={cardContentStyles}>
				<Stack space="space.100">
					<Heading as="h2" size="small">
						{title}
					</Heading>
					<Text as="p" color="color.text.subtle">
						{description}
					</Text>
				</Stack>
				<Inline alignBlock="center" spread="space-between">
					<LoomAttributionLogo appearance="brand" size="medium" />
					{actions}
				</Inline>
			</Stack>
		</Box>
	);

	if (sourceAdType === 'crossFlow') {
		return (
			<PopoverProvider>
				<PopoverTarget>
					{trigger({
						ref: triggerElementRef,
						isSelected: isOpen,
						onBlur: handleTriggerBlur,
						onFocus: handleTriggerFocus,
						onMouseEnter: handleTriggerMouseEnter,
						onMouseLeave: handleMouseLeave,
						'aria-expanded': isOpen,
						'aria-haspopup': true,
					})}
				</PopoverTarget>

				<Box
					ref={triggerElementRef}
					testId="loom-cross-sell-spotlight-content-container"
					onMouseEnter={handleCardMouseEnter}
					onMouseLeave={handleMouseLeave}
					xcss={spotlightContentContainerStyles}
				>
					<PopoverContent
						isVisible={isOpen}
						placement={spotlightPlacement || 'left-start'}
						dismiss={safeClosePopup}
						testId="loom-cross-sell-spotlight-content"
						strategy="absolute"
						shouldDismissOnClickOutside={false} // fixed issue where the popup is dismissed when clicking the 'Why am I seeing this?' modal
					>
						<SpotlightCard testId="loom-cross-sell-spotlight-content-card">
							<SpotlightHeader>
								<Heading as="span" size="xsmall">
									{title}
								</Heading>
								<SpotlightControls>
									<SpotlightDismissControl />
									{adControl}
								</SpotlightControls>
							</SpotlightHeader>
							<SpotlightMedia>
								<AkImage
									alt=""
									src={
										isDarkMode
											? LoomCrossSellPopupCardImageNewVisual_Dark
											: LoomCrossSellPopupCardImageNewVisual_Light
									}
								/>
							</SpotlightMedia>
							<SpotlightBody>
								<Text as="p">{description}</Text>
							</SpotlightBody>
							<SpotlightFooter>
								<SpotlightActions>
									<SpotlightPrimaryAction onClick={primaryOnClick}>
										{intl.formatMessage(i18n.tryNow)}
									</SpotlightPrimaryAction>
								</SpotlightActions>
							</SpotlightFooter>
						</SpotlightCard>
					</PopoverContent>
				</Box>
			</PopoverProvider>
		);
	}

	return (
		<Popup
			autoFocus={false}
			content={content}
			isOpen={isOpen}
			/**
			 * Using `titleId` and adding the same value as an `id` to the Heading in the popup content
			 * does not announce properly in macOS VoiceOver
			 */
			label={title}
			onClose={safeClosePopup}
			placement={placement || 'auto'}
			role="dialog"
			shouldRenderToParent={shouldRenderToParent}
			trigger={(popupTriggerProps) =>
				trigger({
					...popupTriggerProps,
					isSelected: isOpen,
					onBlur: handleTriggerBlur,
					onFocus: handleTriggerFocus,
					onMouseEnter: handleTriggerMouseEnter,
					onMouseLeave: handleMouseLeave,
					ref: mergeRefs([popupTriggerProps.ref, triggerElementRef]),
				})
			}
		/>
	);
};
